import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  IconButton,
  TextField,
  Button,
  MenuItem,
  Chip,
  TableSortLabel,
  Tooltip,
  Select,
  FormControl,
  InputLabel,
  Box,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MoveToInboxIcon from "@mui/icons-material/MoveToInbox";
import { Link, useNavigate } from "react-router-dom";
import {
  GetSupplyOrders,
  GetSupplyOrderByPoNumber,
  DeleteSupplyOrder,
} from "../../../services/InventoryService";
import { GetVendors } from "../../../services/GeneralMaintenanceService";
import {
  supplyOrderStatusList,
  supplyOrderPriorityList,
} from "../../common/statusLists";
import CommonLoader from "../../common/CommonLoader";
import ViewSupplyOrder from "./ViewSupplyOrder";
import DeleteConfirmation from "../../common/DeleteConfirmation";
import { NotificationManager } from "react-notifications";
import ReceiveSupplyOrderDialog from "./ReceiveSupplyOrderDialog";
import { stableSort } from "../../../../common-utilities/sortingUtils";

// yyyy-MM-dd in local time for <input type="date">; toISOString would shift the day in UTC+ zones.
const toDateInputValue = (date) => {
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )}`;
};

const daysFromToday = (days) => {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return toDateInputValue(date);
};

const SupplyOrdersTable = () => {
  const navigate = useNavigate();

  // field is the order property the header sorts on; Actions has none so it isn't sortable.
  // Dates stacks order, ship-by and expected in one cell and sorts on order date.
  const columns = [
    { label: "PO #", field: "poNumber" },
    { label: "Vendor", field: "vendorName" },
    { label: "Status", field: "status" },
    { label: "Priority", field: "priority" },
    { label: "Order Type", field: "orderType" },
    { label: "Dates", field: "orderDate" },
    { label: "Tracking #", field: "trackingNumber" },
    { label: "Total", field: "totalAmount" },
    { label: "Notes", field: "notes" },
    { label: "Actions" },
  ];
  const rowsPerPageOptions = [10, 25, 50, 100, 200];
  const notesMaxWidth = 180;
  const stripeColor = "#f5f5f5";

  // Short values stay on one line so rows don't grow tall.
  const noWrap = { whiteSpace: "nowrap" };

  // Actions stays pinned to the right edge while the rest of the table scrolls under it.
  const stickyActions = {
    position: "sticky",
    right: 0,
    zIndex: 1,
    boxShadow: "-2px 0 4px rgba(0, 0, 0, 0.08)",
  };

  const [ordersData, setOrdersData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [vendorFilter, setVendorFilter] = useState("");
  // Default window is today -45 / +15 days; an order shows if its Ordered, Ship by or Expected date falls inside.
  const [fromDate, setFromDate] = useState(() => daysFromToday(-45));
  const [toDate, setToDate] = useState(() => daysFromToday(15));
  const [vendors, setVendors] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    useState(false);
  const [selectedItemToDelete, setSelectedItemToDelete] = useState(null);
  const [receiveOrder, setReceiveOrder] = useState(null);
  const [orderBy, setOrderBy] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");

  // Load once with the default criteria; after that only Search (or Enter) fetches,
  // so changing a filter doesn't fire an API call.
  useEffect(() => {
    fetchVendors();
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchVendors = async () => {
    try {
      const response = await GetVendors();
      setVendors(response?.results ?? []);
    } catch (error) {
      console.error("Error fetching vendors:", error);
      NotificationManager.error("Error fetching vendors");
    }
  };

  // statusFilter is a comma separated list, which is what the API expects (same as Lab Orders).
  const getSelectedStatuses = () =>
    statusFilter ? statusFilter.split(",").filter((s) => s.trim() !== "") : [];

  const handleStatusSelect = (status) => {
    const current = getSelectedStatuses();
    if (status && !current.includes(status)) {
      setStatusFilter([...current, status].join(","));
    }
  };

  const handleRemoveStatus = (status) => {
    setStatusFilter(
      getSelectedStatuses()
        .filter((s) => s !== status)
        .join(",")
    );
  };

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await GetSupplyOrders(
        statusFilter,
        searchTerm,
        priorityFilter,
        vendorFilter,
        fromDate,
        toDate
      );
      setOrdersData(response.results);
      setPage(0);
    } catch (error) {
      console.error("Error fetching supply orders:", error);
      NotificationManager.error("Error fetching supply orders");
    } finally {
      setLoading(false);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    fetchOrders();
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (field) => {
    const isAsc = orderBy === field && sortOrder === "asc";
    setSortOrder(isAsc ? "desc" : "asc");
    setOrderBy(field);
    setPage(0);
  };

  // Priority sorts by urgency rather than alphabetically.
  const priorityRank = (priority) => {
    const rank = supplyOrderPriorityList.indexOf(priority);
    return rank === -1 ? 0 : rank;
  };

  // Blanks always sink to the bottom, and text compares case-insensitively and
  // numerically so PO 2026010 lands after 2026009. Dates are ISO strings, so
  // plain text comparison already orders them correctly.
  const compareOrders = (a, b) => {
    let x = a[orderBy];
    let y = b[orderBy];
    const xBlank = x === null || x === undefined || x === "";
    const yBlank = y === null || y === undefined || y === "";
    if (xBlank || yBlank) {
      return xBlank === yBlank ? 0 : xBlank ? 1 : -1;
    }

    if (orderBy === "priority") {
      x = priorityRank(x);
      y = priorityRank(y);
    }

    const result =
      typeof x === "number" && typeof y === "number"
        ? x - y
        : String(x).localeCompare(String(y), undefined, {
            numeric: true,
            sensitivity: "base",
          });
    return sortOrder === "desc" ? -result : result;
  };

  // Sort the whole result set, not just the visible page, since everything is loaded client side.
  const sortedOrders = orderBy
    ? stableSort(ordersData, compareOrders)
    : ordersData;

  const handleAddOrder = () => {
    navigate("/vlp/inventory-supply-orders/add");
  };

  // New tab so the search criteria here survive the edit (ENB-605).
  // No "noopener": the edit page uses window.opener to close itself on Close.
  const handleEditClick = (order) => {
    window.open(`/vlp/inventory-supply-orders/${order.poNumber}`, "_blank");
  };

  const loadOrder = async (poNumber) => {
    const response = await GetSupplyOrderByPoNumber(poNumber);
    return response.results;
  };

  const handleViewClick = async (order) => {
    try {
      setLoading(true);
      setSelectedOrder(await loadOrder(order.poNumber));
    } catch (error) {
      console.error("Error fetching supply order:", error);
      NotificationManager.error("Error fetching supply order");
    } finally {
      setLoading(false);
    }
  };

  const handleReceiveClick = async (order) => {
    try {
      setLoading(true);
      setReceiveOrder(await loadOrder(order.poNumber));
    } catch (error) {
      console.error("Error fetching supply order:", error);
      NotificationManager.error("Error fetching supply order");
    } finally {
      setLoading(false);
    }
  };

  const handleBackClick = () => {
    setSelectedOrder(null);
  };

  const handleDeleteClick = (order) => {
    setSelectedItemToDelete(order);
    setIsDeleteConfirmationOpen(true);
  };

  const handleDeleteConfirmationClose = () => {
    setIsDeleteConfirmationOpen(false);
  };

  const handleDeleteConfirmationConfirm = async () => {
    try {
      await DeleteSupplyOrder(selectedItemToDelete.poNumber);
      setIsDeleteConfirmationOpen(false);
      setOrdersData(
        ordersData.filter(
          (item) => item.poNumber !== selectedItemToDelete.poNumber
        )
      );
      NotificationManager.success(
        `PO ${selectedItemToDelete.poNumber} deleted successfully`
      );
    } catch (error) {
      setIsDeleteConfirmationOpen(false);
      console.error("Error deleting supply order:", error);
      NotificationManager.error("Error deleting supply order");
    }
  };

  const formatDate = (value) =>
    value ? new Date(value).toLocaleDateString() : "";

  const formatMoney = (value) =>
    value !== null && value !== undefined ? `$${Number(value).toFixed(2)}` : "";

  // Colour only the states someone needs to act on, so the grid stays readable.
  const statusColor = (status) => {
    switch (status) {
      case "Received in Full":
      case "Released":
        return "success";
      case "Received Partially":
      case "QC Required":
      case "Waiting on Final Payment":
        return "warning";
      case "Cancelled":
        return "error";
      case "Pending":
      case "Closed":
        return "default";
      default:
        return "primary";
    }
  };

  // Standard stays neutral so Elevated and Urgent stand out in the grid.
  const priorityColor = (priority) => {
    switch (priority) {
      case "Urgent":
        return "error";
      case "Elevated":
        return "warning";
      default:
        return "default";
    }
  };

  // Notes stay on one line, ellipsised by CSS at notesMaxWidth, with the full text in a tooltip.
  const renderNotes = (notes) => {
    if (!notes) {
      return notes;
    }
    return (
      <Tooltip title={notes} arrow>
        <div
          style={{
            maxWidth: notesMaxWidth,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            cursor: "default",
          }}
        >
          {notes}
        </div>
      </Tooltip>
    );
  };

  // Only dates that are set get a line, so most rows stay one line tall.
  const renderDates = (order) => {
    const dates = [
      { label: "Ordered", value: order.orderDate },
      { label: "Ship by", value: order.mustShipByDate },
      { label: "Expected", value: order.expectedDate },
    ].filter((date) => date.value);
    return dates.map((date) => (
      <div key={date.label} style={noWrap}>
        <span style={{ color: "rgba(0, 0, 0, 0.6)", fontSize: "0.75rem" }}>
          {date.label}:
        </span>{" "}
        {formatDate(date.value)}
      </div>
    ));
  };

  const renderActions = (order) => (
    <div style={{ minWidth: "120px", whiteSpace: "nowrap" }}>
      <IconButton
        aria-label="edit"
        size="small"
        style={{ color: "orange" }}
        onClick={() => handleEditClick(order)}
      >
        <EditIcon fontSize="small" />
      </IconButton>
      <IconButton
        aria-label="view"
        size="small"
        style={{ color: "green" }}
        onClick={() => handleViewClick(order)}
      >
        <VisibilityIcon fontSize="small" />
      </IconButton>
      <IconButton
        aria-label="receive"
        size="small"
        title="Receive stock"
        style={{ color: "#1976d2" }}
        onClick={() => handleReceiveClick(order)}
      >
        <MoveToInboxIcon fontSize="small" />
      </IconButton>
      <IconButton
        aria-label="delete"
        size="small"
        style={{ color: "red" }}
        onClick={() => handleDeleteClick(order)}
      >
        <DeleteIcon fontSize="small" />
      </IconButton>
    </div>
  );

  return (
    <div className="vlp-component-container">
      {/* New sits on the title row so the filter row has room for the date range. */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginRight: "20px",
        }}
      >
        <span className="title">Supply Orders </span>
        {!selectedOrder && (
          <Button
            variant="outlined"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAddOrder}
          >
            New
          </Button>
        )}
      </div>
      <hr />
      {selectedOrder ? (
        <>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ArrowBackIcon />}
            onClick={handleBackClick}
            style={{ marginBottom: "16px", color: "green" }}
          >
            Back
          </Button>
          <ViewSupplyOrder order={selectedOrder} />
        </>
      ) : (
        <>
          {/* A form, like Lab Orders, so Enter in any text or date field runs the search. */}
          <form
            onSubmit={handleSearchSubmit}
            style={{
              display: "flex",
              marginBottom: "16px",
              alignItems: "center",
              gap: "12px",
            }}
          >
            <Tooltip
              title="Search by PO #, tracking #, vendor invoice # or product name / item number. Press Enter to search"
              arrow
              placement="top"
            >
              <TextField
                label="Search PO #, tracking, invoice or product"
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ width: "20%" }}
              />
            </Tooltip>
            <Tooltip
              title="Shows orders with an Ordered, Ship by or Expected date on or after this date"
              arrow
              placement="top"
            >
              <TextField
                label="From"
                type="date"
                variant="outlined"
                size="small"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: toDate || undefined }}
                style={{ width: "11%" }}
              />
            </Tooltip>
            <Tooltip
              title="Shows orders with an Ordered, Ship by or Expected date on or before this date"
              arrow
              placement="top"
            >
              <TextField
                label="To"
                type="date"
                variant="outlined"
                size="small"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: fromDate || undefined }}
                style={{ width: "11%" }}
              />
            </Tooltip>
            <Tooltip
              title="Filter by vendor"
              arrow
              placement="top"
            >
              <TextField
                select
                label="Vendor"
                variant="outlined"
                size="small"
                value={vendorFilter}
                onChange={(e) => setVendorFilter(e.target.value)}
                style={{ width: "15%" }}
              >
                <MenuItem value="">All</MenuItem>
                {vendors.map((vendor) => (
                  <MenuItem key={vendor.recordId} value={vendor.recordId}>
                    {vendor.vendorName}
                  </MenuItem>
                ))}
              </TextField>
            </Tooltip>
            {/* Pick one status at a time; picked ones show as removable chips below. */}
            <Tooltip
              title="Select one or multiple statuses"
              arrow
              placement="top"
            >
              <FormControl
                variant="outlined"
                size="small"
                style={{ width: "13%" }}
              >
                <InputLabel shrink>Status</InputLabel>
                <Select
                  value=""
                  onChange={(e) => handleStatusSelect(e.target.value)}
                  label="Status"
                  notched
                  displayEmpty
                  renderValue={() => {
                    const selectedCount = getSelectedStatuses().length;
                    return selectedCount > 0
                      ? `${selectedCount} status${
                          selectedCount > 1 ? "es" : ""
                        } selected`
                      : "All";
                  }}
                >
                  {supplyOrderStatusList.map((status) => (
                    <MenuItem
                      key={status}
                      value={status}
                      disabled={getSelectedStatuses().includes(status)}
                    >
                      {status}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Tooltip>
            <Tooltip
              title="Filter by priority"
              arrow
              placement="top"
            >
              <TextField
                select
                label="Priority"
                variant="outlined"
                size="small"
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value)}
                style={{ width: "10%" }}
              >
                <MenuItem value="">All</MenuItem>
                {supplyOrderPriorityList.map((priority) => (
                  <MenuItem key={priority} value={priority}>
                    {priority}
                  </MenuItem>
                ))}
              </TextField>
            </Tooltip>
            <Button variant="contained" color="primary" type="submit">
              Search
            </Button>
          </form>
          {getSelectedStatuses().length > 0 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
                mb: 2,
              }}
            >
              <strong style={{ fontSize: "0.875rem" }}>
                Selected Statuses:
              </strong>
              {getSelectedStatuses().map((status) => (
                <Chip
                  key={status}
                  label={status}
                  onDelete={() => handleRemoveStatus(status)}
                  color="secondary"
                  variant="outlined"
                  size="small"
                />
              ))}
              <Button size="small" onClick={() => setStatusFilter("")}>
                Clear
              </Button>
            </Box>
          )}
          <Paper style={{ marginRight: "20px" }}>
            <TableContainer>
              <Table sx={{ minWidth: 400 }} size="small">
                <TableHead
                  sx={{
                    backgroundColor: (theme) => theme.palette.primary.main,
                    color: "white",
                  }}
                >
                  <TableRow>
                    {columns.map((column) =>
                      column.field ? (
                        <TableCell
                          key={column.label}
                          sx={noWrap}
                          sortDirection={
                            orderBy === column.field ? sortOrder : false
                          }
                        >
                          <TableSortLabel
                            active={orderBy === column.field}
                            direction={
                              orderBy === column.field ? sortOrder : "asc"
                            }
                            onClick={() => handleRequestSort(column.field)}
                          >
                            {column.label}
                          </TableSortLabel>
                        </TableCell>
                      ) : (
                        <TableCell
                          key={column.label}
                          sx={{
                            ...noWrap,
                            ...stickyActions,
                            backgroundColor: (theme) =>
                              theme.palette.primary.main,
                          }}
                        >
                          {column.label}
                        </TableCell>
                      )
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedOrders
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((order, index) => {
                      // Solid stripe colour (not rgba) so the sticky Actions cell hides what scrolls beneath it.
                      const rowColor = index % 2 === 0 ? stripeColor : "white";
                      return (
                        <TableRow
                          key={order.poNumber}
                          sx={{ backgroundColor: rowColor }}
                        >
                          <TableCell sx={noWrap}>
                            {/* Same route as the edit icon; a real link so ctrl/middle-click opens a new tab. */}
                            <Link
                              to={`/vlp/inventory-supply-orders/${order.poNumber}`}
                              onClick={(e) => {
                                // Leave ctrl/shift/middle-click to the browser.
                                if (e.ctrlKey || e.metaKey || e.shiftKey || e.button !== 0) return;
                                e.preventDefault();
                                handleEditClick(order);
                              }}
                              style={{ color: "#1976d2", fontWeight: 500 }}
                            >
                              {order.poNumber}
                            </Link>
                          </TableCell>
                          <TableCell>{order.vendorName}</TableCell>
                          <TableCell sx={noWrap}>
                            <Chip
                              label={order.status}
                              size="small"
                              color={statusColor(order.status)}
                            />
                          </TableCell>
                          <TableCell sx={noWrap}>
                            <Chip
                              label={order.priority}
                              size="small"
                              color={priorityColor(order.priority)}
                            />
                          </TableCell>
                          <TableCell sx={noWrap}>{order.orderType}</TableCell>
                          <TableCell>{renderDates(order)}</TableCell>
                          <TableCell sx={noWrap}>{order.trackingNumber}</TableCell>
                          <TableCell sx={noWrap}>
                            {formatMoney(order.totalAmount)}
                          </TableCell>
                          <TableCell>{renderNotes(order.notes)}</TableCell>
                          <TableCell
                            sx={{ ...stickyActions, backgroundColor: rowColor }}
                          >
                            {renderActions(order)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={rowsPerPageOptions}
              component="div"
              count={ordersData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
          <CommonLoader loading={loading} />
        </>
      )}

      <DeleteConfirmation
        isOpen={isDeleteConfirmationOpen}
        onClose={handleDeleteConfirmationClose}
        onConfirm={handleDeleteConfirmationConfirm}
        title="Delete Confirmation"
        message={`Are you sure you want to delete PO ${selectedItemToDelete?.poNumber}?`}
        color="red"
      />
      <ReceiveSupplyOrderDialog
        open={Boolean(receiveOrder)}
        order={receiveOrder}
        onClose={() => setReceiveOrder(null)}
        onSave={fetchOrders}
      />
    </div>
  );
};

export default SupplyOrdersTable;
